import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Libraries } from '@react-google-maps/api';
import { _GoogleAddress } from '../../../constants/staticTypes';
import { initialAddress } from '../../../helpers/commonHelper';
import './MlxGoogleMaps.scss';
import logger from '../../../utils/logger';
import { useTranslation } from '../../../hooks/useTranslation';
import { LOADING, SEARCH_PLACE } from '../../../constants/strings';

const containerStyle = {
    height: '350px',
};

const languageTexts = {
    SEARCH_PLACE: SEARCH_PLACE,
    LOADING: LOADING,
};

type Props = {
    addressCallback: (address: _GoogleAddress) => void;
    address: _GoogleAddress;
    style?: React.CSSProperties;
};

const libraries: Libraries = ['places', 'marker'];

// AdvancedMarkerElement only renders on a map that carries a Map ID; DEMO_MAP_ID is Google's
// no-styling placeholder for accounts that haven't provisioned a real one yet.
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const mapOptions: google.maps.MapOptions = { mapId: GOOGLE_MAPS_MAP_ID };

// The `gmp-select` event isn't in @types/google.maps yet (it types the older gmp-placeselect
// shape), so its detail is narrowed from `unknown` instead of typed end-to-end.
type PlaceSelectEventDetail = { placePrediction: google.maps.places.PlacePrediction | null };

function MlxGoogleMaps({ addressCallback, address, style }: Props) {
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const { lat, lng } = address;

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>({ lat: lat!, lng: lng! });
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const placeAutocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
    const lastGeocodedCoordsRef = useRef<{ lat: number; lng: number } | null>(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });

    const extractAddressFromGeocoderResult = (mapData: google.maps.GeocoderResult | null): _GoogleAddress => {
        const location = mapData?.geometry?.location;
        if (!mapData?.address_components || !location) {
            return initialAddress;
        }
        const extracted: _GoogleAddress = { ...initialAddress };
        const { address_components, place_id, formatted_address } = mapData;

        address_components.forEach((component) => {
            const types = component?.types;
            extracted.lat = location.lat();
            extracted.lng = location.lng();
            extracted.place_id = place_id;
            extracted.formatted_address = formatted_address;

            if (types?.includes('locality')) {
                extracted.city_town = component?.long_name || '';
            } else if (types?.includes('administrative_area_level_1')) {
                extracted.state = component?.long_name || '';
            } else if (types?.includes('country')) {
                extracted.country_region = component?.long_name || '';
            } else if (types?.includes('postal_code')) {
                extracted.zip_code = component?.long_name || '';
            }
        });

        return extracted;
    };

    const extractAddressFromPlace = (place: google.maps.places.Place): _GoogleAddress => {
        const location = place.location;
        if (!place.addressComponents || !location) {
            return initialAddress;
        }
        const extracted: _GoogleAddress = { ...initialAddress };
        extracted.lat = location.lat();
        extracted.lng = location.lng();
        extracted.place_id = place.id;
        extracted.formatted_address = place.formattedAddress ?? undefined;

        place.addressComponents.forEach((component) => {
            const types = component?.types;
            if (types?.includes('locality')) {
                extracted.city_town = component?.longText || '';
            } else if (types?.includes('administrative_area_level_1')) {
                extracted.state = component?.longText || '';
            } else if (types?.includes('country')) {
                extracted.country_region = component?.longText || '';
            } else if (types?.includes('postal_code')) {
                extracted.zip_code = component?.longText || '';
            }
        });

        return extracted;
    };

    const getAddress = async (lat: number, lng: number) => {
        // Skip re-geocoding coordinates we already resolved (e.g. via a suggestion click),
        // otherwise the [lat, lng, map] effect below re-fires on the resulting state update
        // and a slow/failed reverse-geocode call can wipe out the fields it just filled in.
        if (lastGeocodedCoordsRef.current?.lat === lat && lastGeocodedCoordsRef.current?.lng === lng) {
            return;
        }
        lastGeocodedCoordsRef.current = { lat, lng };
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.status === 'OK' && data.results[0]) {
                const addressData = extractAddressFromGeocoderResult(data.results[0]);
                addressCallback(addressData);
            } else {
                addressCallback(initialAddress);
            }
        } catch (error) {
            logger.error('Error fetching address:', error);
        }
    };

    const onLoadMap = useCallback((mapInstance: google.maps.Map) => {
        const bounds = new window.google.maps.LatLngBounds(markerPosition);
        mapInstance.fitBounds(bounds);
        mapRef.current = mapInstance;
        setMap(mapInstance);
    }, []);

    const onUnmountMap = useCallback(() => {
        mapRef.current = null;
        setMap(null);
    }, []);

    const handleMapClick = (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setMarkerPosition({ lat, lng });
            getAddress(lat, lng);
        }
    };

    const handlePlaceSelect = useCallback(
        async (event: Event) => {
            const { placePrediction } = event as unknown as PlaceSelectEventDetail;
            if (!placePrediction) {
                return;
            }

            const place = placePrediction.toPlace();
            await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location', 'id'] });

            const location = place.location;
            if (!location) {
                return;
            }
            const lat = location.lat();
            const lng = location.lng();

            // Mark these coords as already resolved so the [lat, lng, map] effect
            // below doesn't re-geocode them and clobber the fields we're about to set.
            lastGeocodedCoordsRef.current = { lat, lng };

            // Build the address directly from the selected place instead of making a
            // separate Geocoding API call, which can fail independently of Places.
            addressCallback(extractAddressFromPlace(place));
            setMarkerPosition({ lat, lng });

            const mapInstance = mapRef.current;
            if (mapInstance) {
                mapInstance.panTo({ lat, lng });
                mapInstance.setZoom(15);
            }
        },
        [addressCallback]
    );

    // Mounts the PlaceAutocompleteElement web component into the container div once the Maps
    // script has loaded; it manages its own input and suggestions dropdown internally.
    useEffect(() => {
        if (!isLoaded || !autocompleteContainerRef.current || placeAutocompleteRef.current) {
            return;
        }

        let isCancelled = false;

        (async () => {
            // google.maps.importLibrary('places') types its return as a library union that
            // omits PlaceAutocompleteElement, so import for the side effect only and construct
            // off the namespace class, which is fully and separately typed.
            await google.maps.importLibrary('places');
            if (isCancelled || !autocompleteContainerRef.current) {
                return;
            }

            const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({});
            placeAutocomplete.setAttribute('placeholder', translatedText.SEARCH_PLACE);
            placeAutocomplete.addEventListener('gmp-select', handlePlaceSelect);
            autocompleteContainerRef.current.appendChild(placeAutocomplete);
            placeAutocompleteRef.current = placeAutocomplete;
        })();

        return () => {
            isCancelled = true;
            if (placeAutocompleteRef.current) {
                placeAutocompleteRef.current.removeEventListener('gmp-select', handlePlaceSelect);
                placeAutocompleteRef.current.remove();
                placeAutocompleteRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded]);

    useEffect(() => {
        placeAutocompleteRef.current?.setAttribute('placeholder', translatedText.SEARCH_PLACE);
    }, [translatedText.SEARCH_PLACE]);

    // Creates the AdvancedMarkerElement once the map is ready, then just moves it on later
    // position changes instead of tearing it down and recreating it.
    useEffect(() => {
        if (!isLoaded || !map) {
            return;
        }

        let isCancelled = false;

        (async () => {
            // The global google.maps.importLibrary (unlike the Loader instance method) types its
            // return as a union of every library regardless of the name passed in, hence the cast.
            const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
            if (isCancelled) {
                return;
            }

            if (!markerRef.current) {
                markerRef.current = new AdvancedMarkerElement({ map, position: markerPosition });
            } else {
                markerRef.current.map = map;
                markerRef.current.position = markerPosition;
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isLoaded, map, markerPosition]);

    useEffect(
        () => () => {
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
        },
        []
    );

    // add a comment for below use effect
    useEffect(() => {
        if (map && lat && lng) {
            getAddress(lat, lng);
            setMarkerPosition({ lat, lng });

            setTimeout(() => {
                map.panTo({ lat, lng });
                map.setZoom(15);
            }, 300);
        }
    }, [lat, lng, map]); // eslint-disable-line

    return isLoaded ? (
        <div className="w-full relative">
            <div className="map-container">
                <div className="map-search" ref={autocompleteContainerRef} />
                <GoogleMap
                    mapContainerStyle={style ?? containerStyle}
                    center={markerPosition}
                    zoom={15}
                    options={mapOptions}
                    onLoad={onLoadMap}
                    onUnmount={onUnmountMap}
                    onClick={handleMapClick}
                />
            </div>

            {/* {address && <p className="mt-2">Address: {JSON.stringify(address)}</p>} */}
        </div>
    ) : (
        <div>{translatedText.LOADING}</div>
    );
}

export default React.memo(MlxGoogleMaps);
